/**
 * De onde vêm os avisos que o banner mostra (#650).
 *
 * O `useAgentExecution` monta `warnings` a partir de eventos SSE, e só. Enquanto
 * a execução roda isso é o que há de mais fresco; depois do reload, é vazio —
 * e era esse o defeito: a run terminava com avisos e a tela não mostrava
 * nenhum. Medido numa execução real em 11/09/2026 (`8dbdde7d`): 5 avisos no
 * `output_summary`, banner vazio.
 *
 * A precedência é a mesma que o `AgentPageLayout` já usa para `outputs` e
 * `metrics`, e a repetição é proposital — três campos com regras diferentes de
 * precedência seria pior que três com a mesma.
 *
 * Run passada escolhida é um corte seco: se ela não tem aviso, o resultado é
 * vazio. Herdar os da última concluída mostraria aviso de OUTRA execução como
 * se fosse dela.
 */

import type { WarningEntry } from "@/types/api";

type ComAvisos = { warnings?: unknown };

/** Um aviso é string solta (a maioria) ou o objeto estruturado ({code, message, params})
 * `message` nunca falta nesse segundo caso. */
function isWarningEntry(v: unknown): v is WarningEntry {
  if (typeof v === "string") return true;
  return (
    typeof v === "object" && v !== null && typeof (v as { message?: unknown }).message === "string"
  );
}

function asList(value: unknown): WarningEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isWarningEntry);
}

export function resolveWarnings({
  live,
  selected,
  latestCompleted,
  isViewingPastRun = false,
}: {
  live: WarningEntry[];
  selected?: ComAvisos | null;
  latestCompleted?: ComAvisos | null;
  isViewingPastRun?: boolean;
}): WarningEntry[] {
  if (isViewingPastRun) return asList(selected?.warnings);
  if (live.length > 0) return live;
  return asList(latestCompleted?.warnings);
}
